import { useState } from 'react'
import Dialog from './Dialog'
import { combinedCog, DEFAULT_BIKE, type BikeParams } from '../analysis/traction'
import { saveBike } from '../lib/bike'

interface Props {
  bike: BikeParams
  onClose: () => void
}

type Field = {
  key: keyof BikeParams
  label: string
  unit: string
  step: number
  /** Shown and typed as a percentage */
  percent?: boolean
}

const RIDER: Field[] = [
  { key: 'riderMass', label: 'Weight with gear', unit: 'kg', step: 1 },
  { key: 'riderCogHeight', label: 'Centre of gravity height, seated', unit: 'm', step: 0.05 },
  { key: 'riderFrontFraction', label: 'Weight on the front wheel', unit: '%', step: 1, percent: true },
]
const BIKE: Field[] = [
  { key: 'bikeMass', label: 'Weight, wet', unit: 'kg', step: 1 },
  { key: 'wheelbase', label: 'Wheelbase', unit: 'm', step: 0.01 },
  { key: 'bikeCogHeight', label: 'Centre of gravity height', unit: 'm', step: 0.05 },
  { key: 'bikeFrontFraction', label: 'Weight on the front wheel', unit: '%', step: 1, percent: true },
  { key: 'engineBrakeG', label: 'Engine braking carried by the rear', unit: 'g', step: 0.05 },
]

/**
 * What the traction circles assume: the model in a sentence, and the bike and rider numbers it
 * runs on, which can be changed. Saved for the app, not per session.
 */
export default function BikeDialog({ bike, onClose }: Props) {
  // Drafts as typed, so a half-typed number is not reformatted under the finger.
  const [draft, setDraft] = useState<Record<keyof BikeParams, string>>(() => toDraft(bike))
  const parsed = fromDraft(draft, bike)
  const cog = combinedCog(parsed)
  const valid = (Object.keys(DEFAULT_BIKE) as (keyof BikeParams)[]).every((k) => Number.isFinite(parsed[k]))

  const field = (f: Field) => (
    <label key={f.key} className="bike-field">
      <span>{f.label}</span>
      <span className="bike-input">
        <input
          type="number"
          inputMode="decimal"
          step={f.step}
          value={draft[f.key]}
          onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
        />
        <small className="muted">{f.unit}</small>
      </span>
    </label>
  )

  return (
    <Dialog title="Traction circle assumptions" onClose={onClose}>
      <p className="small assumptions-text">
        Tyre force over its load, from GPS. Rear drive; front brake with up to{' '}
        {Number.isFinite(parsed.engineBrakeG) ? parsed.engineBrakeG.toFixed(2) : '–'} g engine braking on the rear;
        lateral split by load.
      </p>
      <p className="muted small">
        Bike and rider are taken as one mass: with the numbers below its centre of gravity is{' '}
        {valid ? cog.height.toFixed(2) : '–'} m high with {valid ? Math.round(cog.frontFraction * 100) : '–'}% of the
        weight on the front.
      </p>
      <h4 className="bike-head">Rider</h4>
      {RIDER.map(field)}
      <h4 className="bike-head">Motorcycle</h4>
      {BIKE.map(field)}
      <div className="side-row dialog-buttons">
        <button
          className="button"
          disabled={!valid}
          onClick={() => {
            saveBike(parsed)
            onClose()
          }}
        >
          Save
        </button>
        <button className="button secondary" onClick={() => setDraft(toDraft(DEFAULT_BIKE))}>
          Defaults
        </button>
        <button className="button secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Dialog>
  )
}

const PERCENT = new Set<keyof BikeParams>(['riderFrontFraction', 'bikeFrontFraction'])

function toDraft(p: BikeParams): Record<keyof BikeParams, string> {
  const out = {} as Record<keyof BikeParams, string>
  for (const k of Object.keys(DEFAULT_BIKE) as (keyof BikeParams)[]) {
    const v = p[k]
    out[k] = PERCENT.has(k) ? String(Math.round(v * 100)) : String(v)
  }
  return out
}

function fromDraft(d: Record<keyof BikeParams, string>, fallback: BikeParams): BikeParams {
  const out = { ...fallback }
  for (const k of Object.keys(DEFAULT_BIKE) as (keyof BikeParams)[]) {
    const v = parseFloat(d[k])
    out[k] = Number.isFinite(v) ? (PERCENT.has(k) ? v / 100 : v) : NaN
  }
  return out
}
